import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import {
  encryptForConversation,
  decryptForConversation,
  encryptMediaForConversation,
  decryptMediaForConversation,
  getConversationKeyForEpoch,
  getLatestConversationKey,
  refetchConversationKey,
  prepareDirectConversationKeys,
  prepareSavedMessagesKey,
  prepareGroupConversationKeys,
  sealExistingKeyForNewMember,
  rotateGroupKeyExcludingRemoved,
  cacheNewConversationKey,
  checkConversationKeyStatus,
  DecryptionError
} from "@/lib/e2ee";
import { cacheMessage, getCachedMessages, clearConversationMessages, removeCachedMessage, removeCachedMessages } from "@/lib/local-store";
import { getClearedBeforeTimestamp, setClearedBeforeTimestamp } from "@/lib/local-clear";
import { InvalidPublicKeyError } from "@/lib/crypto";
import type { ConversationSummary, Message, ReadStatusEntry, GroupMember, PinnedMessage, DeliveryStatusEntry, LiveLocationShare, MessageEditHistoryEntry, MutualGroup } from "@/types";

/**
 * Thrown when trying to message/add someone whose account has no public key on
 * file yet - meaning they registered but have never actually logged into a real
 * browser session (only logging in triggers generating and publishing a real E2EE
 * identity; registration alone does not). Carries a `.code` so callers can show
 * this specific, actionable reason instead of it silently being swallowed by a
 * generic catch-all - which is what shipped originally, before this was caught.
 */
function noEncryptionKeyError() {
  const err = new Error("This person hasn't finished setting up encryption yet - ask them to log in once first") as Error & {
    code: string;
  };
  err.code = "no_encryption_key";
  return err;
}

export function useConversations(opts: { archived?: boolean; requests?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.archived) params.set("archived", "true");
  if (opts.requests) params.set("requests", "true");
  const qs = params.toString();
  return useQuery({
    queryKey: ["conversations", opts.archived ?? false, opts.requests ?? false],
    queryFn: () => api.get<ConversationSummary[]>(`/api/conversations${qs ? `?${qs}` : ""}`),
    refetchInterval: 15_000 // light poll as a safety net alongside the websocket
  });
}

/**
 * A single conversation's metadata, regardless of its archive/pin/request status.
 * Use this (not a manual scan across the filtered useConversations() lists) whenever
 * you need "what conversation is this and who's in it" for one specific id - the
 * filtered lists are for the chat list screens, and silently don't contain a
 * conversation that's archived, or someone else's pending request, etc.
 */
export function useConversation(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["conversation", conversationId],
    queryFn: () => api.get<ConversationSummary>(`/api/conversations/${conversationId}`),
    enabled: !!conversationId
  });
}

/** Raw ciphertext rows from the server. Use useDecryptedMessages for display. */
export function useMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["messages", conversationId],
    queryFn: () => api.get<Message[]>(`/api/conversations/${conversationId}/messages`),
    enabled: !!conversationId
  });
}

export interface DecryptedMessage {
  message: Message;
  /** null for image/file messages (decrypted at render time) or if decryption failed. */
  plaintext: string | null;
  decryptionFailed: boolean;
  /** True when there's simply no key for this conversation on this account yet -
   * e.g. joined via an invite link and waiting for an admin to grant access. This
   * is a normal, temporary, expected state, NOT the same as decryptionFailed
   * (a key that exists but doesn't unseal, which usually means a lost identity).
   * Callers must not treat this as "permanently broken." */
  pendingKey: boolean;
}

/**
 * Decrypted, offline-capable view of a conversation's messages: hydrates instantly
 * from the local cache, then reconciles against freshly-fetched ciphertext from the
 * server, decrypting anything new and writing it back to the cache.
 */
export function useDecryptedMessages(conversationId: string | undefined): {
  messages: DecryptedMessage[];
  isLoading: boolean;
} {
  const { data: rawMessages, isLoading: serverLoading } = useMessages(conversationId);
  const [decrypted, setDecrypted] = useState<DecryptedMessage[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // Instant hydration from IndexedDB when the conversation changes
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    setHydrated(false);
    getCachedMessages(conversationId).then((cached) => {
      if (cancelled) return;
      setDecrypted(cached.map((c) => ({ message: c.message, plaintext: c.plaintext, decryptionFailed: false, pendingKey: false })));
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Reconcile with the server's ciphertext once it arrives
  useEffect(() => {
    if (!conversationId || !rawMessages) return;
    let cancelled = false;

    (async () => {
      const results: DecryptedMessage[] = [];
      for (const message of rawMessages) {
        let plaintext: string | null = null;
        let decryptionFailed = false;
        let pendingKey = false;

        // Any message type that carries encrypted text content (text, location,
        // contact - anything using content/nonce rather than only media_url) needs
        // decrypting here. Gating on a specific allowlist of message_type values was
        // the actual bug that shipped: location/contact were added as new types but
        // this check still only recognized "text", so their content silently never
        // got decrypted and they rendered as empty bubbles. Checking for the
        // presence of content+nonce instead can't miss a type again.
        if (message.content && message.nonce) {
          try {
            plaintext = await decryptForConversation(conversationId, message.content, message.nonce, message.key_epoch);
          } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
              // No key on file for this account yet - e.g. joined a group via an
              // invite link and an admin hasn't granted access yet. Normal,
              // temporary, expected - NOT the same as a genuine decrypt failure,
              // and must never be treated as "permanently broken" by callers.
              pendingKey = true;
            } else if (err instanceof DecryptionError) {
              // Could be a genuinely corrupt/tampered message, or it could be that
              // our locally cached key for this conversation is wrong (see
              // useGetOrCreateDirect's comment for how that could happen
              // historically). There's no way to tell those apart without trying
              // the real key, so: force a fresh fetch+unseal from the server and
              // retry exactly once.
              try {
                await refetchConversationKey(conversationId);
                plaintext = await decryptForConversation(conversationId, message.content, message.nonce, message.key_epoch);
              } catch (retryErr) {
                if (retryErr instanceof ApiError && retryErr.status === 404) pendingKey = true;
                else decryptionFailed = true;
              }
            } else {
              // Something unexpected (network blip, 500, etc.) - mark this one
              // message as failed rather than letting it silently abort the whole
              // batch, which used to leave the entire message list stuck.
              decryptionFailed = true;
            }
          }
        }

        await cacheMessage(conversationId, message, plaintext);
        results.push({ message, plaintext, decryptionFailed, pendingKey });
      }
      if (!cancelled) setDecrypted(results);
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId, rawMessages]);

  const clearedBefore = conversationId ? getClearedBeforeTimestamp(conversationId) : null;
  const visibleMessages = clearedBefore
    ? decrypted.filter((m) => new Date(m.message.created_at).getTime() >= clearedBefore)
    : decrypted;

  return { messages: visibleMessages, isLoading: serverLoading && !hydrated };
}

// Pragmatic stopping point for useLoadMessagesUntil's backward paging - bounds
// how long a single permalink resolution can run (50/page * this many pages)
// if the target turns out to be missing (e.g. a bad/stale link) rather than
// paging all the way back through a conversation's entire history.
const LOAD_UNTIL_MAX_PAGES = 100;

/**
 * Pages backward through a conversation's history - via the same before/limit
 * cursor GET /api/conversations/:id/messages already accepts (see the backend's
 * getMessages) - until the given message id shows up in the cached messages, or
 * there's nothing further back left to fetch. The chat screen only ever loads
 * the most recent page up front (see useMessages above), so resolving a
 * permalink to an older message needs this to actually fetch it in, not just
 * scroll into content that was never requested.
 *
 * Merges every page it fetches into the existing ["messages", conversationId]
 * query cache as it goes (oldest-first, matching how useMessages already
 * orders things), so useDecryptedMessages picks the newly-loaded messages up
 * and decrypts them the same way it does everything else - callers don't need
 * to do anything with the resolved messages themselves, just re-check once
 * this resolves whether the target id is now present.
 */
export function useLoadMessagesUntil(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  return async (targetMessageId: string): Promise<boolean> => {
    if (!conversationId) return false;
    try {
      for (let page = 0; page < LOAD_UNTIL_MAX_PAGES; page++) {
        const current = queryClient.getQueryData<Message[]>(["messages", conversationId]) ?? [];
        if (current.some((m) => m.id === targetMessageId)) return true;

        const oldest = current[0]; // ascending order (oldest first) - see getMessages' .reverse()
        const params = new URLSearchParams({ limit: "50" });
        if (oldest) params.set("before", oldest.id);
        const olderPage = await api.get<Message[]>(`/api/conversations/${conversationId}/messages?${params}`);
        if (olderPage.length === 0) return false; // hit the start of history without finding it

        queryClient.setQueryData<Message[]>(["messages", conversationId], (prev) => {
          const merged = [...olderPage, ...(prev ?? [])];
          const seen = new Set<string>();
          return merged.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
        });
        if (olderPage.some((m) => m.id === targetMessageId)) return true;
      }
      return false;
    } catch {
      // A missing/deleted conversation, a message that's since expired
      // (disappearing messages), or any other fetch failure all just mean
      // "couldn't resolve this permalink" from the caller's point of view.
      return false;
    }
  };
}

/**
 * Clears this conversation's history from THIS device's display only - never a
 * server call, never affects the other person, never affects any other device
 * of this account. Sets a local cutoff (persists across reloads, unlike just
 * wiping the cache alone would - messages still exist server-side by design,
 * for multi-device sync, so a plain cache wipe would just get silently undone
 * the next time this device re-fetches) and frees the now-redundant local cache
 * for storage hygiene.
 */
export function useClearChatDisplay() {
  const queryClient = useQueryClient();
  return async (conversationId: string) => {
    setClearedBeforeTimestamp(conversationId, Date.now());
    await clearConversationMessages(conversationId);
    queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });
  };
}

/**
 * Directly checks whether this device has a working key for a conversation -
 * unlike inferring it from message history (which breaks down for a conversation
 * with zero messages yet, see checkConversationKeyStatus's own comment), this
 * works correctly regardless of how much activity the conversation has had.
 * Re-checks whenever the conversation id changes; not on every message, since
 * key status doesn't change from message activity alone.
 */
export function useConversationKeyStatus(conversationId: string | undefined, serverCurrentEpoch: number | undefined) {
  return useQuery({
    queryKey: ["conversation-key-status", conversationId, serverCurrentEpoch],
    queryFn: () => checkConversationKeyStatus(conversationId!, serverCurrentEpoch!),
    enabled: !!conversationId && serverCurrentEpoch !== undefined,
    staleTime: 30_000
  });
}

export function useGetOrCreateDirect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, publicKey }: { userId: string; publicKey: string | null }) => {
      // Check first, with zero crypto involved: if these two already have a
      // conversation, just reopen it. This matters because the other person's
      // CURRENT public key could be broken/invalid (e.g. a test account that's
      // never logged in for real) while their EXISTING conversation's keys, sealed
      // correctly back when it was actually created, are completely unaffected -
      // reopening it should never depend on their current key being valid at all.
      const existing = await api.get<{ conversationId: string | null }>(`/api/conversations/direct/${userId}`);
      let isRestart = false;
      if (existing.conversationId) {
        try {
          // Direct conversations never rotate (rotation only ever happens for
          // group member removal - a direct chat's only equivalent is
          // block/abandon, which deletes the whole conversation instead), so
          // this is always epoch 1.
          await getConversationKeyForEpoch(existing.conversationId, 1);
          return { id: existing.conversationId, restarted: false };
        } catch (err) {
          if (!(err instanceof DecryptionError)) throw err;
          isRestart = true;
          // This device's identity genuinely can't unseal the existing
          // conversation's key - most often a fresh identity after this device
          // lost its original one. The old conversation isn't touched or
          // deleted - it's still fine wherever the correct key actually is (this
          // account's other devices, or the other person's side). This device
          // just needs its own, separate, working conversation to participate at
          // all - falls through to create one below, forcing a genuinely new
          // conversation rather than being handed back the same undecryptable one.
        }
      }

      if (!publicKey) throw noEncryptionKeyError();
      let sealedKeyForMe: string, sealedKeyForThem: string;
      try {
        ({ sealedKeyForMe, sealedKeyForThem } = await prepareDirectConversationKeys(publicKey));
      } catch (err) {
        if (err instanceof InvalidPublicKeyError) throw noEncryptionKeyError();
        throw err;
      }
      const result = await api.post<{ id: string }>("/api/conversations/direct", {
        userId,
        sealedKeyForMe,
        sealedKeyForThem,
        forceNew: isRestart
      });
      // Deliberately NOT caching the key we just generated above: getOrCreateDirect
      // silently reuses an existing conversation (and discards whatever sealed keys
      // we just sent) if these two users already have one. If we'd cached our own
      // freshly-generated key here, it would overwrite the real key every existing
      // message was encrypted with - permanently breaking decryption for the whole
      // conversation, including messages already exchanged. getConversationKey()
      // always fetches+unseals whatever the server actually has on file, which is
      // correct either way (brand new conversation or a pre-existing one) - one
      // extra request for a new conversation, but it's the only version that can't
      // silently desync from reality.
      await getConversationKeyForEpoch(result.id, 1);
      return { ...result, restarted: isRestart };
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

/**
 * Saved Messages: a self-chat for notes/bookmarks. The backend's own
 * getOrCreateSavedMessages is already idempotent (returns the existing one if
 * this account already has it, ignoring whatever key we send in that case),
 * so this hook stays simple - generate a key, seal it for ourselves, call the
 * endpoint. No DecryptionError recovery logic like useGetOrCreateDirect above:
 * an unrecoverable key here (a genuinely different identity than whichever
 * device first created it) surfaces through this app's normal
 * "couldn't decrypt" handling for any conversation, rather than silently
 * starting a fresh one and orphaning existing saved notes.
 */
export type SavedMessagesOpenResult = {
  id: string;
  /** null = it opened normally. Otherwise what the self-heal did - see rekeySavedMessages on the server. */
  healed: null | "rekeyed" | "replaced" | "created";
  previousId?: string;
};

/** The sentence to show after a self-heal (null when nothing was healed). Plain words, says what was kept. */
export function savedMessagesHealNotice(result: SavedMessagesOpenResult): string | null {
  if (result.healed === "replaced") {
    return "Your previous Saved Messages can't be opened on this device, so a new one was started. The old notes are kept - they can be read again from the device you first used them on.";
  }
  if (result.healed === "rekeyed") {
    return "Saved Messages was reset because its old encryption key isn't on this device. It was empty, so nothing was lost.";
  }
  return null;
}

/**
 * Opens (or creates) Saved Messages and makes sure THIS device can actually read it - it never leaves the person stuck.
 * If the stored key can't be opened here (an encryption identity that was replaced after the chat was made), it asks the
 * server to repair it: an empty chat is re-keyed in place; a chat with notes is kept and set aside and a new one is started.
 * Nothing is deleted. A fresh key is only ever sealed for the identity on THIS device, which is the one that just proved it
 * can't read the old one.
 */
export function useGetOrCreateSavedMessages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SavedMessagesOpenResult> => {
      const { sealedKeyForMe } = await prepareSavedMessagesKey();
      const opened = await api.post<{ id: string }>("/api/conversations/saved-messages", { sealedKeyForMe });
      try {
        await getConversationKeyForEpoch(opened.id, 1);
        return { id: opened.id, healed: null };
      } catch (err) {
        const unreadable = err instanceof DecryptionError || (err instanceof ApiError && err.status === 404);
        if (!unreadable) throw err;
      }
      const repaired = await api.post<{ id: string; action: "current" | "created" | "rekeyed" | "replaced"; previousId?: string }>(
        "/api/conversations/saved-messages/rekey",
        { conversationId: opened.id, sealedKeyForMe }
      );
      try {
        await refetchConversationKey(repaired.id);
        await getConversationKeyForEpoch(repaired.id, 1);
      } catch (err) {
        if (err instanceof DecryptionError) {
          throw new Error(
            "Your Saved Messages are protected by an encryption key that isn't on this device. Sign in on the device you first used, or set up recovery in Security settings."
          );
        }
        throw err;
      }
      return {
        id: repaired.id,
        healed: repaired.action === "current" ? null : repaired.action,
        previousId: repaired.previousId
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
      // This screen and the chat list remember "this one is unreadable here" (a cached verdict that otherwise lasts until a reload).
      queryClient.invalidateQueries({ queryKey: ["conversation-key-status"] });
      queryClient.setQueryData(["conversation-undecryptable", result.id], false);
    }
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; members: { userId: string; publicKey: string | null }[]; broadcastOnly?: boolean }) => {
      const withoutKeys = input.members.filter((m) => !m.publicKey);
      if (withoutKeys.length > 0) throw new Error("Some members haven't finished setting up encryption yet - ask them to log in once first");

      let key: Uint8Array, members: { userId: string; sealedKey: string }[];
      try {
        ({ key, members } = await prepareGroupConversationKeys(
          input.members.map((m) => ({ userId: m.userId, publicKey: m.publicKey! }))
        ));
      } catch (err) {
        if (err instanceof InvalidPublicKeyError) {
          throw new Error("One of the selected members hasn't finished setting up encryption yet - ask them to log in once first");
        }
        throw err;
      }
      const result = await api.post<{ id: string }>("/api/conversations/group", {
        name: input.name,
        members,
        broadcastOnly: input.broadcastOnly
      });
      await cacheNewConversationKey(result.id, key);
      return result;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useSendMessage(conversationId: string, serverCurrentEpoch: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      content?: string;
      messageType?: "text" | "image" | "file" | "location" | "contact" | "voice" | "poll" | "poll_vote" | "sticker" | "payment" | "payment_request" | "payment_split" | "video";
      mediaUrl?: string;
      replyToId?: string;
      forwarded?: boolean;
      /** ISO string - if set and in the future, this is stored but not shown to
       * anyone (including the sender's own chat view) until it becomes due. */
      scheduledAt?: string;
      /** Direct conversations only - see the backend's own comment on why this
       * can't have well-defined semantics for a group. */
      viewOnce?: boolean;
      storyReplyId?: string;
      pollDurationSeconds?: 3600 | 86400 | 259200 | 604800 | null;
      /** Only for messageType "payment" - the wallet-service ledger entry
       * this message is the receipt for, already created by a prior
       * transfer call before this mutation runs. */
      ledgerEntryId?: string;
      /** Only for messageType "payment_request"/"payment_split" - the
       * wallet-service request/split row this message announces, already
       * created by a prior wallet call before this mutation runs. */
      paymentRequestId?: string;
      splitGroupId?: string;
      /** For media messages (image/voice/etc.) - the epoch used to encrypt
       * the media BYTES, from a separate encryptMediaForConversation call
       * that already happened before this mutation runs (media is encrypted
       * client-side and uploaded first, then just its URL is sent here).
       * Without this, a media-only message (no `content`) would have no way
       * to tell the server which epoch it actually used, since the epoch-
       * resolving branch below only runs when there's text content to
       * encrypt. */
      keyEpoch?: number;
    }) => {
      const body: {
        content?: string;
        nonce?: string;
        messageType?: string;
        mediaUrl?: string;
        replyToId?: string;
        forwarded?: boolean;
        scheduledAt?: string;
        viewOnce?: boolean;
        storyReplyId?: string;
        pollDurationSeconds?: 3600 | 86400 | 259200 | 604800 | null;
        keyEpoch?: number;
        ledgerEntryId?: string;
        paymentRequestId?: string;
        splitGroupId?: string;
      } = {
        messageType: input.messageType,
        mediaUrl: input.mediaUrl,
        replyToId: input.replyToId,
        forwarded: input.forwarded,
        scheduledAt: input.scheduledAt,
        viewOnce: input.viewOnce,
        storyReplyId: input.storyReplyId,
        pollDurationSeconds: input.pollDurationSeconds,
        keyEpoch: input.keyEpoch,
        ledgerEntryId: input.ledgerEntryId,
        paymentRequestId: input.paymentRequestId,
        splitGroupId: input.splitGroupId
      };
      if (input.content) {
        const { content, nonce, keyEpoch } = await encryptForConversation(conversationId, serverCurrentEpoch, input.content);
        body.content = content;
        body.nonce = nonce;
        body.keyEpoch = keyEpoch;
      }
      const message = await api.post<Message>(`/api/conversations/${conversationId}/messages`, body);
      return { message, isScheduled: !!input.scheduledAt };
    },
    onSuccess: async ({ message, isScheduled }) => {
      if (isScheduled) {
        // Not sent yet - matches the backend excluding it from the normal message
        // list for everyone, including the sender, until it's actually due.
        // Caching it here or refreshing the main message list would make it show
        // up early, on this device, contradicting "hasn't been sent yet."
        queryClient.invalidateQueries({ queryKey: ["scheduled-messages", conversationId] });
        return;
      }
      // Best-effort optimistic local cache write - a failure here just means
      // this cache write is skipped; the message still exists server-side
      // and will be picked up (and gracefully marked decryptionFailed if
      // still unreadable) by decryptOne's own already-safe retry path on
      // next load.
      let plaintext: string | null = null;
      if (message.content && message.nonce) {
        try {
          plaintext = await decryptForConversation(conversationId, message.content, message.nonce, message.key_epoch);
        } catch {
          // swallow - see comment above
        }
      }
      await cacheMessage(conversationId, message, plaintext);
      queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

/**
 * A thread's messages belong to a conversation the person is already
 * successfully viewing when they'd open one - the key is proven working by
 * that point - so this uses a simpler best-effort decrypt rather than the
 * main timeline's full pendingKey/retry machinery, matching how
 * useForwardMessage above already keeps things simple for a similar reason.
 */
export function useThreadReplies(conversationId: string | undefined, messageId: string | undefined) {
  const query = useQuery({
    queryKey: ["thread", conversationId, messageId],
    queryFn: () => api.get<{ parent: Message; replies: Message[] }>(`/api/conversations/${conversationId}/messages/${messageId}/thread`),
    enabled: !!conversationId && !!messageId
  });

  const [decrypted, setDecrypted] = useState<{ parent: DecryptedMessage; replies: DecryptedMessage[] } | null>(null);

  useEffect(() => {
    if (!conversationId || !query.data) {
      setDecrypted(null);
      return;
    }
    let cancelled = false;

    async function decryptOne(message: Message): Promise<DecryptedMessage> {
      let plaintext: string | null = null;
      let decryptionFailed = false;
      if (message.content && message.nonce) {
        try {
          plaintext = await decryptForConversation(conversationId!, message.content, message.nonce, message.key_epoch);
        } catch {
          decryptionFailed = true;
        }
      }
      return { message, plaintext, decryptionFailed, pendingKey: false };
    }

    (async () => {
      const parent = await decryptOne(query.data!.parent);
      const replies = await Promise.all(query.data!.replies.map(decryptOne));
      if (!cancelled) setDecrypted({ parent, replies });
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId, query.data]);

  return { data: decrypted, isLoading: query.isLoading };
}

export interface DecryptedEditHistoryEntry {
  entry: MessageEditHistoryEntry;
  plaintext: string | null;
  decryptionFailed: boolean;
}

/**
 * A message's prior versions, oldest to newest - not fetched until a dialog
 * for it is actually open (see `enabled`), since most messages are never
 * inspected this way. Same simpler best-effort decrypt as useThreadReplies
 * above, for the same reason: reaching this dialog at all already proves the
 * conversation key works.
 */
export function useMessageEditHistory(conversationId: string | undefined, messageId: string | undefined, enabled: boolean) {
  const query = useQuery({
    queryKey: ["message-edit-history", conversationId, messageId],
    queryFn: () => api.get<MessageEditHistoryEntry[]>(`/api/conversations/${conversationId}/messages/${messageId}/history`),
    enabled: !!conversationId && !!messageId && enabled
  });

  const [decrypted, setDecrypted] = useState<DecryptedEditHistoryEntry[] | null>(null);

  useEffect(() => {
    if (!conversationId || !query.data) {
      setDecrypted(null);
      return;
    }
    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        query.data!.map(async (entry): Promise<DecryptedEditHistoryEntry> => {
          let plaintext: string | null = null;
          let decryptionFailed = false;
          if (entry.content && entry.nonce) {
            try {
              plaintext = await decryptForConversation(conversationId!, entry.content, entry.nonce, entry.key_epoch);
            } catch {
              decryptionFailed = true;
            }
          }
          return { entry, plaintext, decryptionFailed };
        })
      );
      if (!cancelled) setDecrypted(results);
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId, query.data]);

  return { data: decrypted, isLoading: query.isLoading };
}

/**
 * Marks a view-once message as viewed - only ever call this after the person
 * has actually seen the decrypted media, since the server deletes the
 * underlying file the moment this succeeds and there's no undo.
 */
export function useMarkViewOnceViewed(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/api/conversations/${conversationId}/messages/${messageId}/view`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["messages", conversationId] })
  });
}

/** Send a message to a DIFFERENT conversation than the one currently open - used for forwarding. */
export function useForwardMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      targetConversationId,
      targetCurrentEpoch,
      content,
      comment
    }: {
      targetConversationId: string;
      /** The target conversation's own current_key_epoch, from whatever
       * already-fetched conversations list the caller (ForwardMessageDialog)
       * is picking targets from - needed to encrypt correctly for THAT
       * conversation, not the one currently open. */
      targetCurrentEpoch: number;
      content: string;
      /** Optional short note - sent as its own separate, normal (non-forwarded)
       * text message just before the forwarded one, never merged into it. Same
       * per-target encryption as the forwarded content below, just a plain
       * sendMessage-shaped POST with no `forwarded` flag. See
       * ForwardMessageDialog for where this comes from. */
      comment?: string;
    }) => {
      if (comment) {
        const { content: encComment, nonce: commentNonce, keyEpoch: commentEpoch } = await encryptForConversation(
          targetConversationId,
          targetCurrentEpoch,
          comment
        );
        const commentMessage = await api.post<Message>(`/api/conversations/${targetConversationId}/messages`, {
          content: encComment,
          nonce: commentNonce,
          keyEpoch: commentEpoch
        });
        // Best-effort - same reasoning as the other optimistic-cache sites in
        // this file: a failure here doesn't lose the comment, it's already
        // saved server-side, this is purely a local display shortcut.
        let commentPlaintext: string | null = null;
        try {
          commentPlaintext = await decryptForConversation(
            targetConversationId,
            commentMessage.content!,
            commentMessage.nonce!,
            commentMessage.key_epoch
          );
        } catch {
          // swallow - see comment above
        }
        await cacheMessage(targetConversationId, commentMessage, commentPlaintext);
      }

      const { content: encContent, nonce, keyEpoch } = await encryptForConversation(targetConversationId, targetCurrentEpoch, content);
      const message = await api.post<Message>(`/api/conversations/${targetConversationId}/messages`, {
        content: encContent,
        nonce,
        keyEpoch,
        forwarded: true
      });
      // Best-effort - same reasoning as the other optimistic-cache sites in
      // this file.
      let plaintext: string | null = null;
      try {
        plaintext = await decryptForConversation(targetConversationId, message.content!, message.nonce!, message.key_epoch);
      } catch {
        // swallow - see comment above
      }
      await cacheMessage(targetConversationId, message, plaintext);
      return message;
    },
    onSuccess: (_message, { targetConversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["messages", targetConversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

export interface PostSharePayload {
  type: "post_share";
  postId: string;
  authorName: string;
  preview: string;
  mediaUrl?: string;
  mediaType?: "image" | "video";
}

/**
 * A quick one-tap story reaction (see StoryViewerDialog's StoryQuickReactions).
 * Sent through the exact same mechanism as a full typed story reply - a
 * normal encrypted message to the story's poster with story_reply_id set -
 * the only difference is the plaintext content is this small JSON payload
 * instead of freeform text, so MessageBubble can render it distinctly (a
 * single large emoji, not a text bubble) and the recipient can tell it apart
 * from a real typed reply.
 */
export interface StoryReactionPayload {
  type: "story_reaction";
  emoji: string;
}

/**
 * Shares a post into a chat as a structured JSON payload (still fully E2EE - it's
 * just ordinary encrypted text content whose plaintext happens to be JSON).
 * MessageBubble detects this shape and renders a small preview card instead of
 * plain text.
 */
export function useSharePostToChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      targetConversationId,
      targetCurrentEpoch,
      post
    }: {
      targetConversationId: string;
      targetCurrentEpoch: number;
      post: { id: string; content: string | null; authorName: string; mediaUrl?: string | null; mediaType?: "image" | "video" };
    }) => {
      const payload: PostSharePayload = {
        type: "post_share",
        postId: post.id,
        authorName: post.authorName,
        preview: (post.content ?? "").slice(0, 200),
        mediaUrl: post.mediaUrl ?? undefined,
        mediaType: post.mediaUrl ? post.mediaType : undefined
      };
      const { content, nonce, keyEpoch } = await encryptForConversation(targetConversationId, targetCurrentEpoch, JSON.stringify(payload));
      const message = await api.post<Message>(`/api/conversations/${targetConversationId}/messages`, { content, nonce, keyEpoch });
      await cacheMessage(targetConversationId, message, JSON.stringify(payload));
      return message;
    },
    onSuccess: (_message, { targetConversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["messages", targetConversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

export function useEditMessage(conversationId: string, serverCurrentEpoch: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageId, content }: { messageId: string; content: string }) => {
      const { content: encContent, nonce, keyEpoch } = await encryptForConversation(conversationId, serverCurrentEpoch, content);
      return api.patch<Message>(`/api/conversations/${conversationId}/messages/${messageId}`, {
        content: encContent,
        nonce,
        keyEpoch
      });
    },
    onSuccess: async (message) => {
      // Best-effort - same reasoning as the other optimistic-cache sites in
      // this file.
      let plaintext: string | null = null;
      if (message.content && message.nonce) {
        try {
          plaintext = await decryptForConversation(conversationId, message.content, message.nonce, message.key_epoch);
        } catch {
          // swallow - see comment above
        }
      }
      await cacheMessage(conversationId, message, plaintext);
      queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

export function useDeleteMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.delete(`/api/conversations/${conversationId}/messages/${messageId}`),
    onSuccess: (_data, messageId) => {
      removeCachedMessage(conversationId, messageId).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

export function useBulkDeleteMessages(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageIds: string[]) =>
      api.post<{ ok: boolean; deletedIds: string[]; skipped: string[] }>(`/api/conversations/${conversationId}/messages/bulk-delete`, { messageIds }),
    onSuccess: (data) => {
      // Only the ids the server actually confirmed deleted - a skipped id
      // (already deleted by someone else, permission denied, etc.) is still
      // real and shouldn't be wiped from the local cache on a guess.
      removeCachedMessages(conversationId, data.deletedIds).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

export function useReactToMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      api.post(`/api/conversations/${conversationId}/messages/${messageId}/react`, { emoji }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["messages", conversationId] })
  });
}

export function useRemoveReaction(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.delete(`/api/conversations/${conversationId}/messages/${messageId}/react`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["messages", conversationId] })
  });
}

export function useSetPinned() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, pinned }: { conversationId: string; pinned: boolean }) =>
      api.post(`/api/conversations/${conversationId}/pin`, { pinned }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

/**
 * Drag-to-reorder among the caller's own currently-pinned conversations only -
 * purely a personal preference, so this never touches other participants'
 * ordering of the same conversations. Pass the FULL desired list of pinned
 * conversation ids in their new order (not just the two that moved) - the
 * backend writes each id's position by its index in this array.
 */
export function useReorderPinnedConversations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationIds: string[]) =>
      api.post("/api/conversations/pins/reorder", { conversationIds }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); }
  });
}

export function useSetMuted() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, muted, durationSeconds }: { conversationId: string; muted: boolean; durationSeconds?: 3600 | 28800 | 604800 }) =>
      api.post(`/api/conversations/${conversationId}/mute`, { muted, durationSeconds }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useReadStatus(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["read-status", conversationId],
    queryFn: () => api.get<ReadStatusEntry[]>(`/api/conversations/${conversationId}/read-status`),
    enabled: !!conversationId,
    refetchInterval: 5_000
  });
}

export function useGroupMembers(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["group-members", conversationId],
    queryFn: () => api.get<GroupMember[]>(`/api/conversations/${conversationId}/members`),
    enabled: !!conversationId
  });
}

/** Groups both the viewer and the given profile's owner currently belong to - see getMutualGroups. */
export function useMutualGroups(userId: string | undefined) {
  return useQuery({
    queryKey: ["mutual-groups", userId],
    queryFn: () => api.get<MutualGroup[]>(`/api/conversations/mutual-groups/${userId}`),
    enabled: !!userId
  });
}

// Group-scoped nickname override - only affects how this one contact is shown
// within this one group, sitting alongside (never replacing) the global
// nickname set via useSetNickname/useClearNickname in useFriends.ts.
export function useSetGroupNickname(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, nickname }: { userId: string; nickname: string }) =>
      api.put(`/api/conversations/${conversationId}/members/${userId}/nickname`, { nickname }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["group-members", conversationId] })
  });
}

export function useClearGroupNickname(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/api/conversations/${conversationId}/members/${userId}/nickname`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["group-members", conversationId] })
  });
}

export interface GroupSticker {
  id: string;
  image_url: string;
  name: string;
  uploaded_by: string;
  created_at: string;
}

export function useGroupStickers(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["group-stickers", conversationId],
    queryFn: () => api.get<GroupSticker[]>(`/api/conversations/${conversationId}/stickers`),
    enabled: !!conversationId
  });
}

export function useAddGroupSticker(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ imageUrl, name }: { imageUrl: string; name: string }) =>
      api.post(`/api/conversations/${conversationId}/stickers`, { imageUrl, name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["group-stickers", conversationId] })
  });
}

export function useDeleteGroupSticker(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stickerId: string) => api.delete(`/api/conversations/${conversationId}/stickers/${stickerId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["group-stickers", conversationId] })
  });
}

export function useMarkRead(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/api/conversations/${conversationId}/read`, { messageId }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useMarkUnread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api.post(`/api/conversations/${conversationId}/mark-unread`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useDeliveryStatus(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["delivery-status", conversationId],
    queryFn: () => api.get<DeliveryStatusEntry[]>(`/api/conversations/${conversationId}/delivery-status`),
    enabled: !!conversationId,
    refetchInterval: 5_000
  });
}

export function useMarkDelivered(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/api/conversations/${conversationId}/delivered`, { messageId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["delivery-status", conversationId] })
  });
}

export function useGetOrCreateInviteCode(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ inviteCode: string }>(`/api/conversations/${conversationId}/invite-code`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] })
  });
}

export function useRevokeInviteCode(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/api/conversations/${conversationId}/invite-code`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] })
  });
}

export function useSetInviteCodeExpiry(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (expiresInSeconds: 86400 | 259200 | 604800 | null) =>
      api.patch(`/api/conversations/${conversationId}/invite-code/expiry`, { expiresInSeconds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] })
  });
}

export function useStartLiveLocationShare(conversationId: string) {
  return useMutation({
    mutationFn: (durationSeconds: number) =>
      api.post<LiveLocationShare>(`/api/conversations/${conversationId}/live-location/start`, { durationSeconds })
  });
}

export function useStopLiveLocationShare() {
  return useMutation({
    mutationFn: (shareId: string) => api.post(`/api/conversations/live-location/${shareId}/stop`)
  });
}

export function useActiveLiveLocationShares(conversationId: string) {
  return useQuery({
    queryKey: ["live-location-active", conversationId],
    queryFn: () => api.get<LiveLocationShare[]>(`/api/conversations/${conversationId}/live-location/active`)
  });
}

export function useJoinByInviteCode() {
  return useMutation({
    mutationFn: (inviteCode: string) =>
      api.post<{ conversationId: string; alreadyMember: boolean; needsKeySeal?: boolean }>(`/api/conversations/join/${inviteCode}`)
  });
}

/** Wipes a direct conversation entirely, for both sides - the deliberate, explicit
 * "give up and start fresh" action for a conversation confirmed unrecoverable on
 * this device (see ChatPage's conversationPermanentlyBroken). */
export function useAbandonConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api.post(`/api/conversations/${conversationId}/abandon`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversations"] })
  });
}

export function useAddGroupMember(conversationId: string, serverCurrentEpoch: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, publicKey }: { userId: string; publicKey: string | null }) => {
      if (!publicKey) throw noEncryptionKeyError();
      let sealedKey: string;
      let keyEpoch: number;
      try {
        ({ sealedKey, keyEpoch } = await sealExistingKeyForNewMember(conversationId, serverCurrentEpoch, publicKey));
      } catch (err) {
        if (err instanceof InvalidPublicKeyError) throw noEncryptionKeyError();
        throw err;
      }
      return api.post(`/api/conversations/${conversationId}/members`, { userId, sealedKey, keyEpoch });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
      queryClient.invalidateQueries({ queryKey: ["group-members", conversationId] });
    }
  });
}

/**
 * Rotates a group's key to exclude a just-removed member - see
 * rotateGroupKeyExcludingRemoved's own comment for the full mechanics. Only
 * meaningful for the "an admin removed someone else" case: the admin
 * initiating this remains a participant the whole time, which
 * assertParticipant on the backend requires. A member voluntarily leaving on
 * their own is a narrower, separate case this doesn't cover - by the time
 * their own removal call finishes, they're no longer a participant
 * themselves and can't be the one to trigger a rotation afterward.
 */
export function useRotateGroupKey(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (remainingMembers: { userId: string; publicKey: string }[]) =>
      rotateGroupKeyExcludingRemoved(conversationId, remainingMembers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

export function useLeaveOrRemoveMember(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/api/conversations/${conversationId}/members/${userId}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useDeleteMessageForMe(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/api/conversations/${conversationId}/messages/${messageId}/delete-for-me`),
    onSuccess: (_data, messageId) => {
      removeCachedMessage(conversationId, messageId).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["messages", conversationId] });
    }
  });
}

export function useSetArchived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, archived }: { conversationId: string; archived: boolean }) =>
      api.post(`/api/conversations/${conversationId}/archive`, { archived }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useSetWallpaper(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (wallpaper: string | null) => api.post(`/api/conversations/${conversationId}/wallpaper`, { wallpaper }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useSetBubbleColor(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bubbleColor: string | null) => api.post(`/api/conversations/${conversationId}/bubble-color`, { bubbleColor }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useSetNotificationSound(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sound: string | null) => api.post(`/api/conversations/${conversationId}/notification-sound`, { sound }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useSetMessageTextSize(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (textSize: "small" | "normal" | "large" | null) =>
      api.post(`/api/conversations/${conversationId}/message-text-size`, { textSize }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useSetDisappearingMessages(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (seconds: 3600 | 86400 | 604800 | null) =>
      api.post(`/api/conversations/${conversationId}/disappearing`, { seconds }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useConversationMedia(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["conversation-media", conversationId],
    queryFn: () => api.get<Message[]>(`/api/conversations/${conversationId}/media`),
    enabled: !!conversationId
  });
}

export function usePinnedMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["pinned-messages", conversationId],
    queryFn: () => api.get<PinnedMessage[]>(`/api/conversations/${conversationId}/pinned`),
    enabled: !!conversationId
  });
}

export function usePinMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/api/conversations/${conversationId}/messages/${messageId}/pin`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pinned-messages", conversationId] })
  });
}

export function useUnpinMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.delete(`/api/conversations/${conversationId}/messages/${messageId}/pin`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pinned-messages", conversationId] })
  });
}

// Distinct from the pinned-messages hooks above - a genuinely different
// feature (one admin-set announcement vs. unlimited participant pins). See
// the 0023 migration's comment for the full reasoning.
export function useChannelAnnouncement(conversationId: string | undefined, hasAnnouncement: boolean) {
  return useQuery({
    queryKey: ["channel-announcement", conversationId],
    queryFn: () => api.get<Message | null>(`/api/conversations/${conversationId}/announcement`),
    enabled: !!conversationId && hasAnnouncement
  });
}

export function useSetChannelAnnouncement(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/api/conversations/${conversationId}/announcement`, { messageId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["channel-announcement", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useClearChannelAnnouncement(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/api/conversations/${conversationId}/announcement`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["channel-announcement", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useStarMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.post(`/api/conversations/${conversationId}/messages/${messageId}/star`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["starred-messages"] })
  });
}

export function useUnstarMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.delete(`/api/conversations/${conversationId}/messages/${messageId}/star`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["starred-messages"] })
  });
}

export function useStarredMessages() {
  return useQuery({
    queryKey: ["starred-messages"],
    queryFn: () => api.get<(Message & { starred_at: string })[]>("/api/conversations/starred")
  });
}

export function useUpdateGroupSettings(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: { name?: string; avatarUrl?: string; description?: string; iconEmoji?: string }) =>
      api.patch(`/api/conversations/${conversationId}`, patch),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useResetGroupAvatar(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/api/conversations/${conversationId}/avatar`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useAcceptMessageRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api.post(`/api/conversations/${conversationId}/accept-request`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useDeclineMessageRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api.post(`/api/conversations/${conversationId}/decline-request`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["conversations"] }); queryClient.invalidateQueries({ queryKey: ["conversation"] }); }
  });
}

export function useMarkAllConversationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean; updated: number }>("/api/conversations/read-all"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversations"] })
  });
}

export function useSetGroupMemberRole(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: "member" | "admin" }) =>
      api.patch(`/api/conversations/${conversationId}/members/${userId}/role`, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["group-members", conversationId] })
  });
}

export function useSetBroadcastOnly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, broadcastOnly }: { conversationId: string; broadcastOnly: boolean }) =>
      api.post(`/api/conversations/${conversationId}/broadcast-only`, { broadcastOnly }),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useSetPublicListed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, publicListed }: { conversationId: string; publicListed: boolean }) =>
      api.post(`/api/conversations/${conversationId}/public-listed`, { publicListed }),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useSetSlowMode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, seconds }: { conversationId: string; seconds: 10 | 30 | 60 | 300 | null }) =>
      api.post(`/api/conversations/${conversationId}/slow-mode`, { seconds }),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useSetRequireRulesAck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, requireRulesAck }: { conversationId: string; requireRulesAck: boolean }) =>
      api.post(`/api/conversations/${conversationId}/require-rules-ack`, { requireRulesAck }),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useAcknowledgeRules(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/api/conversations/${conversationId}/acknowledge-rules`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversation", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useScheduledMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: ["scheduled-messages", conversationId],
    queryFn: () => api.get<Message[]>(`/api/conversations/${conversationId}/scheduled`),
    enabled: !!conversationId
  });
}

export function useCancelScheduledMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.delete(`/api/conversations/${conversationId}/scheduled/${messageId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scheduled-messages", conversationId] })
  });
}

export function useSnoozeConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, durationSeconds }: { conversationId: string; durationSeconds: number | null }) =>
      api.post(`/api/conversations/${conversationId}/snooze`, { durationSeconds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversations"] })
  });
}
